'use client';

import type { ReactElement } from 'react';
import { Fragment } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { FormField } from '@/components/inventory/inventory-form-card/shared/FormField';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { tokens } from '@/lib/design-tokens';
import type { Contact } from '@/lib/services/contacts/types';
import type { ContactFormSectionProps } from './types';
import { useFieldChanges } from '@/components/documents/ai-import/review-card/field-changes-context';

export function ContactInfoSection({
  control,
  mode,
  values,
  clickToCopy = false,
  customAction
}: ContactFormSectionProps): ReactElement {
  const fieldChanges = useFieldChanges();

  if (mode === 'read') {
    const emailChange = fieldChanges?.email;
    const phoneChange = fieldChanges?.phoneNumber;
    const showEmail = !!values?.email && emailChange?.changeType !== 'conflict';
    const showPhone =
      !!values?.phoneNumber && phoneChange?.changeType !== 'conflict';

    return (
      <Fragment>
        {(showEmail || showPhone || customAction) && (
          <div className="flex items-center gap-4">
            {showEmail && (
              <FormField
                label="Email Address"
                value={values!.email!}
                mode="read"
                clickToCopy={clickToCopy}
                changeType={
                  emailChange?.changeType === 'enrichment'
                    ? 'enrichment'
                    : undefined
                }
              />
            )}
            {showPhone && (
              <FormField
                label="Phone Number"
                value={values!.phoneNumber!}
                mode="read"
                clickToCopy={clickToCopy}
                changeType={
                  phoneChange?.changeType === 'enrichment'
                    ? 'enrichment'
                    : undefined
                }
              />
            )}
            {customAction && (
              <div className="flex items-end">{customAction}</div>
            )}
          </div>
        )}
      </Fragment>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      <Controller
        control={control as Control<Partial<Contact>>}
        name="email"
        render={({ field, fieldState }) => (
          <div className={tokens.patterns.form.spacing}>
            <label htmlFor="email" className={tokens.patterns.form.label}>
              Email Address
            </label>
            <Input
              id="email"
              type="email"
              placeholder="Add email address"
              value={(field.value as string) || ''}
              onChange={(e) => field.onChange(e.target.value)}
              onBlur={field.onBlur}
              className={cn(
                tokens.patterns.form.input,
                fieldState.error && 'border-destructive'
              )}
            />
            {fieldState.error?.message && (
              <span className="text-destructive text-sm">
                {fieldState.error.message}
              </span>
            )}
          </div>
        )}
      />

      <Controller
        control={control as Control<Partial<Contact>>}
        name="phoneNumber"
        render={({ field, fieldState }) => (
          <div className={tokens.patterns.form.spacing}>
            <label htmlFor="phoneNumber" className={tokens.patterns.form.label}>
              Phone Number
            </label>
            <Input
              id="phoneNumber"
              type="tel"
              placeholder="Add phone number"
              value={(field.value as string) || ''}
              onChange={(e) => field.onChange(e.target.value)}
              onBlur={field.onBlur}
              className={cn(
                tokens.patterns.form.input,
                fieldState.error && 'border-destructive'
              )}
            />
            {fieldState.error?.message && (
              <span className="text-destructive text-sm">
                {fieldState.error.message}
              </span>
            )}
          </div>
        )}
      />
    </div>
  );
}
